import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Lock, ShieldCheck, Trash2, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '../auth'
import { AppLayout, PageHeader } from '../components/layout/AppLayout'
import { Badge, type Variant } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card, CardBody } from '../components/ui/Card'
import { useConfirm } from '../components/ui/ConfirmDialog'
import { Dialog } from '../components/ui/Dialog'
import { Empty, Skeleton } from '../components/ui/Empty'
import { Input, Label, Select } from '../components/ui/Input'
import { apiClient } from '../lib/api'

type Role = 'owner' | 'admin' | 'dev' | 'viewer'
type User = {
  id: string
  email: string
  name: string
  role: Role
  createdAt: string
  lastLoginAt: string | null
}
type Client = { id: string; name: string; confidential: boolean }

const ASSIGNABLE: Role[] = ['admin', 'dev', 'viewer']

const roleVariant = (r: Role): Variant =>
  r === 'owner' ? 'brand' : r === 'admin' ? 'info' : r === 'viewer' ? 'muted' : 'default'

export function TeamPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'owner' || user?.role === 'admin'
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [createOpen, setCreateOpen] = useState(false)
  const [manageUser, setManageUser] = useState<User | null>(null)
  const usersQ = useQuery({
    queryKey: ['users'],
    queryFn: () => apiClient.get<User[]>('/users'),
    enabled: isAdmin,
  })

  const del = useMutation({
    mutationFn: (id: string) => apiClient.del(`/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })

  async function onDelete(u: User) {
    const ok = await confirm({
      title: `Eliminare ${u.name}?`,
      message: `${u.email} perderà subito l'accesso. L'utente viene disattivato (soft-delete).`,
      confirmLabel: 'Elimina',
      tone: 'danger',
    })
    if (ok) del.mutate(u.id)
  }

  if (!isAdmin) {
    return (
      <AppLayout>
        <PageHeader title="Team" description="Utenti e permessi" />
        <Empty
          title="Accesso riservato"
          description="Solo owner e admin possono gestire gli utenti."
        />
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <PageHeader
        title="Team"
        description="Utenti dell'organizzazione e accessi ai clienti"
        action={
          <Button onClick={() => setCreateOpen(true)}>
            <UserPlus className="size-4" /> Nuovo utente
          </Button>
        }
      />

      {usersQ.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
        </div>
      ) : usersQ.error ? (
        <Empty title="Errore" description={(usersQ.error as Error).message} />
      ) : !usersQ.data || usersQ.data.length === 0 ? (
        <Empty title="Nessun utente" description="Crea il primo collaboratore." />
      ) : (
        <Card>
          <CardBody className="p-0">
            <table className="w-full text-sm">
              <thead className="text-zinc-400 border-b border-zinc-800/70">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Nome</th>
                  <th className="text-left px-4 py-3 font-medium">Email</th>
                  <th className="text-left px-4 py-3 font-medium">Ruolo</th>
                  <th className="text-right px-4 py-3 font-medium">Accessi</th>
                </tr>
              </thead>
              <tbody>
                {usersQ.data.map((u) => (
                  <tr key={u.id} className="border-b border-zinc-800/40">
                    <td className="px-4 py-3 text-zinc-100">{u.name}</td>
                    <td className="px-4 py-3 text-zinc-400">{u.email}</td>
                    <td className="px-4 py-3">
                      <Badge variant={roleVariant(u.role)}>{u.role}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      {u.role === 'owner' ? (
                        <div className="text-right text-xs text-zinc-500">
                          accesso completo · non modificabile
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setManageUser(u)}>
                            <ShieldCheck className="size-4" /> Gestisci
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Elimina ${u.name}`}
                            disabled={del.isPending}
                            onClick={() => onDelete(u)}
                          >
                            <Trash2 className="size-4 text-pink-400" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}

      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      {manageUser && <ManageAccessDialog user={manageUser} onClose={() => setManageUser(null)} />}
    </AppLayout>
  )
}

// ============================================================
// Create user
// ============================================================
function CreateUserDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<Role>('dev')
  const [password, setPassword] = useState('')

  const create = useMutation({
    mutationFn: () =>
      apiClient.post<User>('/users', { email, name, role, password: password || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      setEmail('')
      setName('')
      setRole('dev')
      setPassword('')
      onClose()
    },
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Nuovo utente"
      description="Crea un collaboratore e assegnagli un ruolo."
    >
      <div className="space-y-3">
        <div>
          <Label htmlFor="new-user-name">Nome</Label>
          <Input
            id="new-user-name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            placeholder="Mario Rossi"
          />
        </div>
        <div>
          <Label htmlFor="new-user-email">Email</Label>
          <Input
            id="new-user-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            placeholder="mario@esempio.it"
          />
        </div>
        <div>
          <Label htmlFor="new-user-role">Ruolo</Label>
          <Select
            id="new-user-role"
            value={role}
            onChange={(e) => setRole(e.currentTarget.value as Role)}
          >
            {ASSIGNABLE.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor="new-user-password">Password iniziale</Label>
          <Input
            id="new-user-password"
            type="text"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            placeholder="min. 8 caratteri"
          />
        </div>
        {create.error && <p className="text-sm text-pink-400">{(create.error as Error).message}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending || !email || !name}>
            {create.isPending ? 'Creazione…' : 'Crea utente'}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

// ============================================================
// Manage a user's role + client access
// ============================================================
function ManageAccessDialog({ user, onClose }: { user: User; onClose: () => void }) {
  const qc = useQueryClient()
  const grantsQ = useQuery({
    queryKey: ['user-grants', user.id],
    queryFn: () => apiClient.get<{ clientIds: string[] }>(`/users/${user.id}/client-access`),
  })
  const clientsQ = useQuery({
    queryKey: ['clients-all'],
    queryFn: () => apiClient.get<{ results: Client[] }>('/clients?limit=200'),
  })

  const granted = new Set(grantsQ.data?.clientIds ?? [])

  const invalidate = () => qc.invalidateQueries({ queryKey: ['user-grants', user.id] })

  const grant = useMutation({
    mutationFn: (clientId: string) =>
      apiClient.post(`/users/${user.id}/client-access`, { clientId }),
    onSuccess: invalidate,
  })
  const revoke = useMutation({
    mutationFn: (clientId: string) => apiClient.del(`/users/${user.id}/client-access/${clientId}`),
    onSuccess: invalidate,
  })
  const changeRole = useMutation({
    mutationFn: (role: Role) => apiClient.patch<User>(`/users/${user.id}`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })

  const busy = grant.isPending || revoke.isPending

  return (
    <Dialog open onClose={onClose} title={user.name} description={user.email} size="lg">
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <Label htmlFor="manage-role" className="mb-0">
            Ruolo
          </Label>
          <Select
            id="manage-role"
            value={user.role}
            onChange={(e) => changeRole.mutate(e.currentTarget.value as Role)}
            className="max-w-[160px]"
          >
            {ASSIGNABLE.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <p className="text-sm font-medium text-zinc-200 mb-2">Clienti accessibili</p>
          {clientsQ.isLoading || grantsQ.isLoading ? (
            <Skeleton className="h-24" />
          ) : (
            <div className="space-y-1 max-h-[40vh] overflow-y-auto pr-1">
              {(clientsQ.data?.results ?? []).map((c) => {
                const isGranted = granted.has(c.id)
                return (
                  <label
                    key={c.id}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg border border-zinc-800/60 hover:bg-zinc-800/30"
                  >
                    <input
                      type="checkbox"
                      checked={isGranted}
                      disabled={c.confidential || busy}
                      onChange={() => (isGranted ? revoke.mutate(c.id) : grant.mutate(c.id))}
                    />
                    <span className="flex-1 text-sm text-zinc-100">{c.name}</span>
                    {c.confidential && (
                      <Badge variant="danger">
                        <Lock className="size-3" /> confidenziale
                      </Badge>
                    )}
                  </label>
                )
              })}
            </div>
          )}
          {grant.error && (
            <p className="text-sm text-pink-400 mt-2">{(grant.error as Error).message}</p>
          )}
        </div>

        <div className="flex items-center justify-end border-t border-zinc-800/70 pt-3">
          <Button variant="ghost" onClick={onClose}>
            Chiudi
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
